import type { TodayView, WeekDay } from '@aj/core';
import { Box, Button, HStack, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { useAppData } from '../../data/AppDataProvider.js';
import { useReviewPlan } from '../../data/hooks.js';
import { useT } from '../../providers/I18nProvider.js';
import { ComebackCard } from './ComebackCard.js';
import { ReviewCard } from './ReviewCard.js';
import { Compose } from './Compose.js';
import { Empty } from './Empty.js';
import { HabitList } from './HabitList.js';
import { Header } from './Header.js';
import { LogCard } from './LogCard.js';
import { TasksCard } from './TasksCard.js';
import { WeekCard } from './WeekCard.js';
import { useHomeActions } from './useHomeActions.js';
import { BOTTOM_CLEARANCE } from '../../lib/layout.js';

export type TodayProps = {
  view: TodayView;
  week: WeekDay[];
  onNavigate: (tab: string) => void;
};

/**
 * Home on a phone: "what do I need to do today?" (§6).
 *
 * The habit list is the screen. Greeting and week card are context above it,
 * navigation is quiet below it, and progression is two small numbers in the
 * corner. No history grid — a month of columns on a 360px screen is a
 * thumbnail of a spreadsheet, and the phone's job is the next tick.
 */
export function Today({ view, week, onNavigate }: TodayProps) {
  const t = useT();
  const { features } = useAppData();
  const home = useHomeActions();
  const review = useReviewPlan();
  const [composing, setComposing] = useState(false);

  const empty = view.habits.length === 0;

  return (
    <Box as="main" className="relative min-h-0 flex-1">
      <VStack gap={4} className={`h-full overflow-y-auto px-5 pt-5 ${BOTTOM_CLEARANCE}`}>
        <Header
          view={view}
          onOpenProfile={() => onNavigate('me')}
          onOpenProgress={() => onNavigate('jatra')}
        />

        {/* Above everything the moment it applies — including above the week
            strip, whose empty circles are exactly what a returning student
            does not need to read first. The return is the day's headline. */}
        {view.comeback && <ComebackCard comeback={view.comeback} />}

        {/* Due reviews sit above the habits: the app's most valuable line is
            the one telling you what to go back over, and it is the reason
            spaced retrieval happens at all (§50, day 9). */}
        {review && <ReviewCard plan={review} />}

        {/* Both hidden on day one: a strip of empty circles reads as a strip
            of failures before anything has happened. */}
        {!empty && features.home.showWeekStrip && <WeekCard week={week} goal={view.goal} />}

        {empty && !composing ? (
          <Empty onAdd={() => setComposing(true)} onTemplate={home.addStarterPack} hasGoal={view.goal !== null} />
        ) : (
          <VStack gap={2}>
            {!empty && (
              <>
                <HStack gap={2} align="baseline">
                  <Text variant="overline" tone="subtle">
                    {t('home.today')}
                  </Text>
                  {view.progress.total > 0 && (
                    <Text variant="caption" tone="muted" numeric>
                      {t('home.doneOfTotal', {
                        done: view.progress.done,
                        total: view.progress.total,
                      })}
                    </Text>
                  )}
                </HStack>

                <HabitList
                  habits={view.habits}
                  schedules={home.schedules}
                  label={t('home.habits')}
                  onToggle={home.toggle}
                  onKeepFloor={home.keepFloor}
                  onReorder={home.reorder}
                />
              </>
            )}

            {/* The composer stays open after a submit — the first minute is
                usually three or four habits in a row, not one. */}
            {composing ? (
              <Compose
                labelKey="home.habitName"
                actionKey="home.addHabit"
                onSubmit={home.addHabit}
                onDismiss={() => setComposing(false)}
              />
            ) : (
              <Button
                variant="secondary"
                icon="plus"
                fullWidth
                className="mt-2"
                onClick={() => setComposing(true)}
                data-coach="add-habit"
              >
                {t('home.addHabit')}
              </Button>
            )}
          </VStack>
        )}

        {/* Under the habits, never above: the list is the screen, and today's
            work is what a student adds once the shape of the day is set. */}
        {!empty && features.home.showTasks && (
          <TasksCard tasks={view.tasks} onAdd={home.addTask} onToggle={home.toggleTask} />
        )}

        {/* Evidence under promises. One column on a phone, so the order is the
            argument: what you said you'd do, then what you actually did. */}
        {!empty && view.log && <LogCard log={view.log} />}
      </VStack>

      {/* No floating "+ Add" here, deliberately. Whatever is largest on
          screen is what the app is taken to be for, and a permanent add
          button teaches the optimizer mindset — accumulate habits, keep two.
          Creating one stays where it belongs: the labelled button at the end
          of the list, and the bottom row of the Now sheet. Nobody does it
          more than a few times a year. */}
    </Box>
  );
}
